import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkle, Plus, Trash, CheckCircle } from "@phosphor-icons/react";

const FREQ_LABEL = { one_time: "Einmalig", monthly: "Monatlich", quarterly: "Quartalsweise", yearly: "Jährlich" };

/**
 * AddonServicesPanel — VAS-Katalog pro Mandant.
 * Zeigt eine Liste zubuchbarer Zusatzdienste (Filterwechsel, Desinfektion, etc.)
 * Props: tenant, onChange
 */
export default function AddonServicesPanel({ tenant }) {
  const [services, setServices] = useState([]);
  const [ns, setNs] = useState({ name: "", description: "", price_cents: 990, duration_min: 8, frequency: "one_time" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const refresh = async () => {
    try {
      const r = await api.get("/addon-services");
      setServices(r.data);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
  };
  useEffect(() => { refresh(); }, []);

  const create = async () => {
    if (!ns.name) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      await api.post("/addon-services", ns);
      setNs({ name: "", description: "", price_cents: 990, duration_min: 8, frequency: "one_time" });
      setMsg("VAS angelegt");
      refresh();
    } catch (e) { setErr(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  };

  const toggleActive = async (s) => {
    await api.patch(`/addon-services/${s.service_id}`, { active: !s.active });
    refresh();
  };
  const remove = async (sid) => {
    if (!confirm("VAS wirklich löschen?")) return;
    await api.delete(`/addon-services/${sid}`);
    refresh();
  };
  const patchField = async (sid, patch) => {
    await api.patch(`/addon-services/${sid}`, patch);
    refresh();
  };

  const eur = (c) => `€${(c / 100).toFixed(2)}`;

  return (
    <div className="bg-white rounded-2xl border border-black/5 p-6 max-w-4xl" data-testid="addon-panel">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Sparkle size={20} weight="bold" style={{ color: "var(--tonneva-primary)" }} />
          <h3 className="font-heading text-xl font-bold tracking-tight">Zusatzdienste (VAS)</h3>
        </div>
      </div>
      <p className="text-sm text-zinc-500 mb-5">Angebote wie Filterwechsel, Innendesinfektion oder Duftaufsatz — Kunden können diese im Portal zubuchen.</p>

      <div className="grid md:grid-cols-6 gap-2 mb-6 p-3 border border-dashed border-black/15 rounded-lg">
        <Input placeholder="Name (z. B. Filterwechsel)" className="col-span-2 h-10 rounded-md" value={ns.name} onChange={(e) => setNs({ ...ns, name: e.target.value })} data-testid="addon-new-name" />
        <Input placeholder="Beschreibung" className="col-span-2 h-10 rounded-md" value={ns.description} onChange={(e) => setNs({ ...ns, description: e.target.value })} data-testid="addon-new-desc" />
        <Input type="number" min="0" step="0.01" placeholder="Preis €" className="h-10 rounded-md" value={(ns.price_cents / 100).toFixed(2)} onChange={(e) => setNs({ ...ns, price_cents: Math.round(parseFloat(e.target.value || 0) * 100) })} data-testid="addon-new-price" />
        <Input type="number" min="1" max="60" placeholder="Min" className="h-10 rounded-md" value={ns.duration_min} onChange={(e) => setNs({ ...ns, duration_min: parseInt(e.target.value) || 5 })} data-testid="addon-new-duration" />
        <Select value={ns.frequency} onValueChange={(v) => setNs({ ...ns, frequency: v })}>
          <SelectTrigger className="h-10 rounded-md col-span-3" data-testid="addon-new-freq"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(FREQ_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Button className="rounded-md text-white col-span-3 h-10" style={{ background: "var(--tonneva-gradient)" }} onClick={create} disabled={busy || !ns.name} data-testid="addon-create">
          <Plus size={14} weight="bold" className="mr-1" /> VAS anlegen
        </Button>
      </div>

      {msg && <div className="text-sm text-emerald-700 bg-emerald-50 rounded-md p-2.5 mb-3" data-testid="addon-msg"><CheckCircle size={14} weight="bold" className="inline mr-1" /> {msg}</div>}
      {err && <div className="text-sm text-red-600 bg-red-50 rounded-md p-2.5 mb-3" data-testid="addon-error">{err}</div>}

      <div className="space-y-2">
        {services.length === 0 ? (
          <div className="py-8 text-center text-zinc-400 text-sm border border-dashed border-black/10 rounded-lg">
            Noch keine Zusatzdienste angelegt.
          </div>
        ) : services.map((s) => (
          <div key={s.service_id} className={`grid md:grid-cols-6 gap-2 items-center p-3 border rounded-md ${s.active ? "border-black/10" : "border-black/5 bg-zinc-50 opacity-70"}`} data-testid={`addon-row-${s.service_id}`}>
            <div className="md:col-span-2">
              <div className="font-semibold text-sm">{s.name}</div>
              {s.description && <div className="text-xs text-zinc-500">{s.description}</div>}
            </div>
            <div className="text-xs">
              <span className="text-zinc-500">Preis:</span> <span className="font-mono font-semibold">{eur(s.price_cents)}</span>
            </div>
            <div className="text-xs">
              <span className="text-zinc-500">Dauer:</span> {s.duration_min} min
            </div>
            <div className="text-xs">
              <span className="text-zinc-500">Rhythm.:</span> {FREQ_LABEL[s.frequency]}
            </div>
            <div className="flex items-center gap-1 justify-end">
              <label className="flex items-center gap-1 text-xs cursor-pointer">
                <input type="checkbox" checked={s.active} onChange={() => toggleActive(s)} data-testid={`addon-active-${s.service_id}`} />
                aktiv
              </label>
              <button onClick={() => remove(s.service_id)} className="text-red-600 hover:bg-red-50 rounded p-1.5" data-testid={`addon-del-${s.service_id}`}><Trash size={12} weight="bold" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
